/**
 * useContactPicker - Search hook for the shared ContactPicker
 *
 * Uses Typesense for fast, fuzzy, typo-tolerant search across the unified
 * contacts collection (fullName, email, phone, companyName, title).
 *
 * @example
 * const { data, isLoading } = useContactPickerSearch('haschka');
 */
import { useQuery } from '@tanstack/react-query';
import { searchRoleGatedOne } from '../lib/search/typesenseScopedSearch';
import { useOrganization } from '../contexts/OrganizationContext';
import { useModule } from '../contexts/ModuleContext';
import type { Contact } from '../types/lead';
import { Timestamp } from 'firebase/firestore';

/** Result item from ContactPicker search — every hit comes from the contacts collection */
export interface ContactPickerResult {
  /** The unified Contact */
  contact: Contact;
  /** Where this result came from. Only the contacts collection is searched (#4587). */
  origin: 'contacts';
}

export const contactPickerKeys = {
  all: ['contactPicker'] as const,
  search: (term: string, orgId?: string, canViewCandidates?: boolean) =>
    [...contactPickerKeys.all, 'search', term, orgId ?? '', canViewCandidates ?? false] as const,
};

/** Map a Typesense hit document to a Contact */
function mapTypesenseHitToContact(doc: Record<string, unknown>): Contact {
  const now = Timestamp.now();
  return {
    // The contacts index carries contactId (no `id`); `id` kept for any older hit shape.
    contactId: (doc.contactId as string) || (doc.id as string) || '',
    organizationId: (doc.organizationId as string) || '',
    firstName: (doc.firstName as string) || '',
    lastName: (doc.lastName as string) || '',
    fullName: (doc.fullName as string) || '',
    email: (doc.email as string) || '',
    phone: (doc.phone as string) || '',
    mobilePhone: (doc.mobilePhone as string) || undefined,
    companyName: (doc.companyName as string) || '',
    title: (doc.title as string) || '',
    source: (doc.source as Contact['source']) || undefined,
    // The contacts index holds neither `sourceId` nor `createdBy` (schemas.ts): these were always empty.
    sourceId: undefined,
    createdAt: now,
    updatedAt: now,
    createdBy: '',
  };
}

/**
 * Search contacts via Typesense — fuzzy, typo-tolerant, multi-field
 *
 * @param searchTerm - Minimum 2 characters required to trigger search
 * @param maxResults - Maximum number of results to return (default: 10)
 * @returns React Query result with ContactPickerResult[]
 */
export function useContactPickerSearch(searchTerm: string, maxResults = 10) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId;
  const { hasModuleAccess } = useModule();
  // Recruiting candidates must not appear in the shared contact picker for
  // users without P14 (Recruiting) access (#2559).
  const canViewCandidates = hasModuleAccess('P14');

  return useQuery({
    queryKey: contactPickerKeys.search(searchTerm, orgId, canViewCandidates),
    queryFn: async (): Promise<ContactPickerResult[]> => {
      // The server (search_searchRestricted) scopes the search to the caller's org and, from the session,
      // adds `isCandidate:=false` for anyone without recruiting access (#3228): the candidate barrier is
      // not decided in the browser. Null = the server did not search contacts for this caller.
      const result = await searchRoleGatedOne('contacts', searchTerm, maxResults);
      if (!result) return [];

      return result.hits.map((hit) => ({
        contact: mapTypesenseHitToContact(hit.document),
        origin: 'contacts' as const,
      }));
    },
    enabled: searchTerm.length >= 2 && !!orgId,
    staleTime: 30_000,
  });
}
